<!-- eslint-disable vue/multi-word-component-names -->
<template>
  <div>
    <div class="page-head">
      <div>
        <h2>支出流水</h2>
        <p>
          记录日常支出、用途、归属分部和支付账户；公司级支出不自动分摊到分部
        </p>
      </div>
      <el-button
        type="primary"
        @click="dialog = true"
      >
        <el-icon><Plus /></el-icon>
        录入支出
      </el-button>
    </div>
    <el-card shadow="never">
      <el-table
        :data="rows"
        v-loading="loading"
        stripe
      >
        <el-table-column
          prop="expense_date"
          label="支出日期"
          width="170"
        />
        <el-table-column
          prop="purpose"
          label="用途"
          min-width="180"
        />
        <el-table-column
          prop="category"
          label="分类"
          width="120"
        />
        <el-table-column
          prop="branch_name"
          label="归属"
          width="130"
        >
          <template #default="{ row }">
            {{ row.branch_name || '公司级' }}
          </template>
        </el-table-column>
        <el-table-column
          prop="account_name"
          label="支付账户"
          width="130"
        />
        <el-table-column
          prop="amount"
          label="金额"
          width="140"
        >
          <template #default="{ row }">¥ {{ money(row.amount) }}</template>
        </el-table-column>
        <el-table-column
          prop="payee"
          label="收款对象"
          width="130"
        />
        <el-table-column
          prop="status"
          label="状态"
          width="160"
        >
          <template #default="{ row }">
            <StatusTag :status="row.status" />
          </template>
        </el-table-column>
        <el-table-column
          label="操作"
          width="200"
        >
          <template #default="{ row }">
            <el-button
              v-if="row.status === 'DRAFT'"
              link
              type="primary"
              @click="confirmRow(row)"
            >
              确认
            </el-button>
            <el-button
              v-if="row.status === 'CONFIRMED'"
              link
              type="danger"
              @click="voidRow(row)"
            >
              作废
            </el-button>
            <VoucherButton
              biz-type="EXPENSE"
              :biz-id="row.id"
            />
          </template>
        </el-table-column>
      </el-table>
      <el-empty
        v-if="!loading && !rows.length"
        description="暂无支出"
      />
      <div class="pager">
        <el-pagination
          v-model:current-page="page"
          v-model:page-size="pageSize"
          layout="total,prev,pager,next"
          :total="total"
          @current-change="load"
        />
      </div>
    </el-card>
    <el-dialog
      v-model="dialog"
      title="录入支出"
      width="560px"
    >
      <el-form
        :model="form"
        label-width="110px"
      >
        <el-form-item
          label="支出日期"
          required
        >
          <el-date-picker
            v-model="form.expenseDate"
            type="datetime"
            value-format="YYYY-MM-DD HH:mm:ss"
            style="width: 100%"
          />
        </el-form-item>
        <el-form-item
          label="金额"
          required
        >
          <el-input-number
            v-model="form.amount"
            :min="0"
            :precision="4"
            :controls="false"
            style="width: 100%"
          />
        </el-form-item>
        <el-form-item label="分类">
          <el-select
            v-model="form.category"
            allow-create
            filterable
            style="width: 100%"
          >
            <el-option
              v-for="c in categories.filter((v) => v.enabled)"
              :key="c.id"
              :label="c.name"
              :value="c.name"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="归属分部">
          <el-select
            v-model="form.organizationUnitId"
            clearable
            style="width: 100%"
          >
            <el-option
              v-for="b in branches.filter((v) => v.enabled)"
              :key="b.id"
              :label="b.name"
              :value="b.id"
            />
          </el-select>
        </el-form-item>
        <el-form-item
          label="支付账户"
          required
        >
          <el-select
            v-model="form.paymentAccountId"
            style="width: 100%"
          >
            <el-option
              v-for="a in accounts.filter((v) => v.enabled)"
              :key="a.id"
              :label="a.name"
              :value="a.id"
            />
          </el-select>
        </el-form-item>
        <el-form-item
          label="用途"
          required
        >
          <el-input
            v-model="form.purpose"
            type="textarea"
          />
        </el-form-item>
        <el-form-item label="支付方式">
          <el-select
            v-model="form.paymentMethod"
            style="width: 100%"
          >
            <el-option
              v-for="m in methods.filter((v) => v.enabled)"
              :key="m.code"
              :label="m.name"
              :value="m.code"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="收款对象">
          <el-input v-model="form.payee" />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="dialog = false">取消</el-button>
        <el-button
          type="primary"
          :loading="saving"
          @click="save"
        >
          保存草稿
        </el-button>
      </template>
    </el-dialog>
  </div>
</template>
<script setup>
import { onMounted, reactive, ref } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import { ledgerApi, organizationApi } from '../api'
import VoucherButton from '../components/VoucherButton.vue'
import StatusTag from '../components/StatusTag.vue'
const rows = ref([]),
  loading = ref(false),
  saving = ref(false),
  dialog = ref(false),
  page = ref(1),
  pageSize = ref(20),
  total = ref(0),
  accounts = ref([]),
  branches = ref([]),
  methods = ref([]),
  categories = ref([]),
  form = reactive({
    expenseDate: '',
    amount: 0,
    category: '',
    paymentAccountId: null,
    organizationUnitId: null,
    purpose: '',
    paymentMethod: 'BANK',
    payee: '',
  })
const money = (v) =>
  Number(v || 0).toLocaleString('zh-CN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
const load = async () => {
  loading.value = true
  try {
    const d = await ledgerApi.expenses({
      page: page.value,
      pageSize: pageSize.value,
    })
    rows.value = d.items || []
    total.value = d.total || 0
  } catch (e) {
    ElMessage.error(e?.message || '加载失败')
  } finally {
    loading.value = false
  }
}
const save = async () => {
  if (
    !form.expenseDate ||
    !form.amount ||
    !form.purpose ||
    !form.paymentAccountId
  ) {
    ElMessage.warning('请填写日期、金额、用途和支付账户')
    return
  }
  saving.value = true
  try {
    await ledgerApi.createExpense(form)
    ElMessage.success('支出草稿已保存')
    dialog.value = false
    load()
  } catch (e) {
    ElMessage.error(e?.message || '保存失败')
  } finally {
    saving.value = false
  }
}
const confirmRow = async (row) => {
  try {
    await ElMessageBox.confirm(
      '确认支出后将形成账户资金流出，是否继续？',
      '确认'
    )
    await ledgerApi.confirmExpense(row.id)
    ElMessage.success('已确认')
    load()
  } catch (e) {
    if (e !== 'cancel') ElMessage.error(e?.message || '确认失败')
  }
}
const voidRow = async (row) => {
  try {
    const { value } = await ElMessageBox.prompt('请输入作废原因', '作废支出', {
      inputValidator: (v) => !!v?.trim() || '原因不能为空',
    })
    await ledgerApi.voidExpense(row.id, value)
    ElMessage.success('支出已作废并生成反向流水')
    load()
  } catch (e) {
    if (e !== 'cancel') ElMessage.error(e?.message || '作废失败')
  }
}
onMounted(() => {
  form.expenseDate = new Date().toISOString().slice(0, 19).replace('T', ' ')
  Promise.all([
    ledgerApi.accounts(),
    organizationApi.branches(),
    organizationApi.paymentMethods(),
    ledgerApi.expenseCategories(),
  ]).then((data) => {
    accounts.value = data[0] || []
    branches.value = data[1] || []
    methods.value = data[2] || []
    categories.value = data[3] || []
  })
  load()
})
</script>
<style scoped>
.page-head {
  display: flex;
  justify-content: space-between;
  margin-bottom: 20px;
}
.page-head h2 {
  font-size: 24px;
}
.page-head p {
  color: #8a97a8;
  margin-top: 6px;
}
.el-card {
  border: 0;
}
.pager {
  display: flex;
  justify-content: flex-end;
  margin-top: 18px;
}
</style>
